import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { authService } from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { getMsalInstance, consumeRedirectResult, loginRequest } from '../config/msalConfig';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const navigate = useNavigate();

    const logout = useCallback(() => {
        setUser(null);
        setMustChangePassword(false);
        localStorage.removeItem('jwtToken');
        delete authService.defaults.headers.common['Authorization'];
        navigate('/');
    }, [navigate]);

    const handleSsoLogin = useCallback((token) => {
        localStorage.setItem('jwtToken', token);
        try {
            const decoded = jwtDecode(token);
            const userData = {
                username: decoded.sub,
                role: decoded.role,
                companyId: decoded.companyId,
            };
            setUser(userData);
            authService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return userData;
        } catch (error) {
            console.error('Error decoding SSO token:', error);
            logout();
            return null;
        }
    }, [logout]);

    useEffect(() => {
        const initAuth = async () => {
            // 1. Check for Microsoft redirect result — consumed exactly once (module-level flag)
            await getMsalInstance();
            const msResult = consumeRedirectResult();
            if (msResult?.idToken) {
                try {
                    const response = await authService.post('/auth/microsoft', { idToken: msResult.idToken });
                    const { jwtToken } = response.data;
                    handleSsoLogin(jwtToken);
                    setLoading(false);
                    navigate('/dashboard');
                    return;
                } catch (e) {
                    console.error('Microsoft redirect handling failed:', e);
                }
            }

            // 2. Check for existing JWT in storage
            const token = localStorage.getItem('jwtToken');
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    if (decoded.exp * 1000 > Date.now()) {
                        setUser({
                            username: decoded.sub,
                            role: decoded.role,
                            companyId: decoded.companyId,
                        });
                        authService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    } else {
                        logout();
                    }
                } catch (error) {
                    console.error('Failed to decode stored token:', error);
                    logout();
                }
            }
            setLoading(false);
        };

        initAuth();
    }, [logout, handleSsoLogin, navigate]);

    const login = async (username, password) => {
        try {
            const response = await authService.post('/auth/login', { username, password });
            const { jwtToken, mustChangePassword: changePasswordRequired } = response.data;

            localStorage.setItem('jwtToken', jwtToken);
            const decoded = jwtDecode(jwtToken);

            setUser({
                username: decoded.sub,
                role: decoded.role,
                companyId: decoded.companyId,
            });

            setMustChangePassword(changePasswordRequired);
            authService.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
            return { changePasswordRequired };
        } catch (error) {
            throw error.response?.data || new Error('Login failed');
        }
    };

    const loginWithMicrosoft = async () => {
        const msal = await getMsalInstance();
        await msal.loginRedirect(loginRequest);
        // Page navigates away to Microsoft — nothing after this runs
    };

    const loginWithGoogle = async (credential) => {
        const response = await authService.post('/auth/google', { idToken: credential });
        const { jwtToken } = response.data;
        handleSsoLogin(jwtToken);
        navigate('/dashboard');
    };

    const passwordChanged = () => {
        setMustChangePassword(false);
        logout();
    };

    const value = {
        user,
        loading,
        mustChangePassword,
        login,
        logout,
        handleSsoLogin,
        loginWithMicrosoft,
        loginWithGoogle,
        passwordChanged,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};